import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { resolveAssetUrl } from "@/lib/media";

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

export function ToolsUsedByWavai() {
  const { lang, localePath } = useLanguage();
  const t = (en: string, ar?: string) => {
    if (lang === "ar" && ar) return ar;
    return getTranslation(en, lang);
  };

  const { data: tools, isLoading } = useQuery({
    queryKey: ["solutions-used-by-wavai"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("solutions")
        .select("id, title, title_ar, slug, description, description_ar, thumbnail_image")
        .eq("used_by_wavai", true)
        .eq("status", "published")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  if (isLoading || !tools || tools.length === 0) return null;

  return (
    <section className="py-12 lg:py-32 bg-secondary/30">
      <div className="container px-6">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 text-balance">
            {t("The Stack That Powers Wavai", "المنظومة التي تشغّل Wavai")}
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            {t(
              "Our Kuwait team runs on a carefully chosen stack of world-class tools — the same platforms we deploy for clients. Battle-tested to keep operations fast, communication clear and delivery consistently excellent.",
              "يعتمد فريقنا في الكويت على منظومة مختارة بعناية من أفضل الأدوات العالمية — وهي نفس المنصات التي ننشرها لعملائنا. مُجرّبة لضمان عمليات سريعة وتواصل واضح وتسليم استثنائي باستمرار."
            )}
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {tools.map((tool) => {
            const imageSrc = resolveAssetUrl(tool.thumbnail_image);

            return (
              <motion.div key={tool.id} variants={fadeUp}>
                <Link
                  to={localePath(`/solutions/${tool.slug}`)}
                  className="block p-6 rounded-2xl bg-card border border-border shadow-card hover:shadow-glow hover:-translate-y-1 transition-all duration-300 h-full"
                >
                  {tool.thumbnail_image && (
                    <img
                      src={imageSrc}
                      alt={lang === "ar" && tool.title_ar ? tool.title_ar : tool.title}
                      className="h-10 w-auto object-contain mb-6"
                    />
                  )}
                  {tool.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {lang === "ar" && tool.description_ar ? tool.description_ar : tool.description}
                    </p>
                  )}
                  <p className="text-sm font-semibold text-foreground mt-auto">
                    {lang === "ar" && tool.title_ar ? tool.title_ar : tool.title}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
